/**
 * The rows that hide a default-scene save on the route the admin is on.
 *
 * The cascade merges rows from least to most specific (cascadeKeys), and a
 * product row outranks every "*" row. A value saved at the chosen level shows
 * only where no more specific row of the route carries the same field. Such
 * rows are common: a save at the background level (for a crop) stores every
 * selected field there, texts included. A title saved later for the whole set
 * was then written, and the editor still showed the background row's title.
 *
 * So a save also writes each saved value to the more specific rows of the
 * route that carry it — what the admin sees is what loads. Only the fields
 * (and, for per-template records, the keys) a row already carries are written:
 * a row gets no new fields, and rows of other routes do not change.
 */
import {MERGE_FIELDS} from "utils/mergeScenes";
import {cascadeKeys, rowKey, type OverrideRow} from "./cropRowTarget";

/** A row's place in the cascade, as the panel writes it. */
export interface RowAddress {
  product: string;
  layout: string | null;
  background: string | null;
  variant: string | null;
}

export interface ShadowingRowPatch extends RowAddress {
  /** The saved fields this row carries too. */
  fields: string[];
  /** What to write: whole field values, as PATCH replaces a field. */
  sceneData: Record<string, unknown>;
  /** The row's scene data before the write, for Undo. */
  previous: Record<string, unknown>;
}

export interface ShadowingRowsInput {
  overrides: OverrideRow[] | null | undefined;
  channel: string;
  /** The route's product: it decides which rows the cascade reads. */
  product: string;
  type: string;
  set: string | null | undefined;
  layout: string | null | undefined;
  background: string | null | undefined;
  variant?: string | null;
  /** The row the admin's Scope + Level writes. */
  chosen: RowAddress;
  /** The fields the save writes to the chosen row. */
  patch: Record<string, unknown>;
}

// The row key, not scene content.
const META_FIELDS = new Set(["product", "channel", "type", "set"]);
const OBJECT_FIELDS = new Set<string>(MERGE_FIELDS.OBJECT_FIELDS);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const addressKey = (address: RowAddress): string =>
  [
    address.product,
    address.layout || "",
    address.background || "",
    address.variant || "",
  ].join(":");

function rowPatch(
  sceneData: Record<string, unknown>,
  patch: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(patch)) {
    if (META_FIELDS.has(field) || value === undefined) continue;
    const own = sceneData[field];
    if (own === undefined) continue;
    if (OBJECT_FIELDS.has(field) && isRecord(own) && isRecord(value)) {
      const shared = Object.keys(value).filter((key) => Object.hasOwn(own, key));
      if (shared.length === 0) continue;
      const merged = {...own};
      for (const key of shared) merged[key] = value[key];
      out[field] = merged;
      continue;
    }
    out[field] = value;
  }
  return out;
}

/** The writes a save needs on the rows that outrank the chosen one. */
export function shadowingRowPatches({
  overrides,
  channel,
  product,
  type,
  set,
  layout,
  background,
  variant,
  chosen,
  patch,
}: ShadowingRowsInput): ShadowingRowPatch[] {
  if (!overrides?.length) return [];

  const keys = cascadeKeys(product, layout, background, variant);
  const rank = new Map(keys.map((key, index) => [key, index]));
  const chosenRank = rank.get(addressKey(chosen));
  if (chosenRank === undefined) {
    throw new Error(`Default-scene row ${addressKey(chosen)} is not in the route's cascade`);
  }

  const ranked: Array<{row: OverrideRow; rank: number}> = [];
  for (const row of overrides) {
    if (row.channel !== channel || row.type !== type) continue;
    if ((row.set || "") !== (set || "")) continue;
    if (!row.scene_data) continue;
    const rowRank = rank.get(rowKey(row));
    if (rowRank === undefined || rowRank <= chosenRank) continue;
    ranked.push({row, rank: rowRank});
  }
  ranked.sort((a, b) => a.rank - b.rank);

  const result: ShadowingRowPatch[] = [];
  for (const {row} of ranked) {
    const sceneData = rowPatch(row.scene_data!, patch);
    const fields = Object.keys(sceneData);
    if (fields.length === 0) continue;
    result.push({
      product: row.product!,
      layout: row.layout || null,
      background: row.background || null,
      variant: row.variant || null,
      fields,
      sceneData,
      previous: row.scene_data!,
    });
  }
  return result;
}

/** "IJ bg 2", "all products layout 3" — a row in the panel's notes. */
export function rowAddressLabel(address: RowAddress): string {
  const parts = [address.product === "*" ? "all products" : address.product];
  if (address.layout) parts.push(`layout ${address.layout}`);
  if (address.background) parts.push(`bg ${address.background}`);
  if (address.variant) parts.push(`variant ${address.variant}`);
  if (parts.length === 1) parts.push("set");
  return parts.join(" ");
}
